








































































































































































































































































































































































































































































































import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import {
    DxDataGrid,
    DxColumn,
    DxSelection,
    DxEditing,
    DxScrolling,
} from 'devextreme-vue/data-grid';
import DataSource from 'devextreme/data/data_source';
import DevExpress from 'devextreme/bundles/dx.all';

import {
    CONTRACTS,
    FetchServiceContract,
    WindowsServiceContract,
    EventsServiceContract,
    Events,
} from '@/core/contracts';
import { Inject, encodeParams, lookupField } from '@/core/helpers/';
import {
    Stock,
    StructuredResult,
    FreeStockAgreement,
    StocksAction,
    Site,
} from '@/dto';
import {
    removeItemsFromView,
    getModifiedItems,
    checkField,
    columnTemplates,
} from '@/core/components/table';
import { ContextsState, ProductsState, IndicatorsState } from '@/store';
import {
    Table,
    defaultTableConfig,
    TableConfig,
} from '@/core/components/table';
import { localize } from '@/core/filters';
import { StocksPageArgs, StocksActions } from './types';
import Query from 'devextreme/data/query';
import ArrayStore from 'devextreme/data/array_store';
import { MicroeconomicsPageArgs } from '../microeconomics';

@Component({
    components: {
        DxSelection,
        DxDataGrid,
        DxColumn,
        DxEditing,
        DxScrolling,
    },
})
export default class StocksTable extends Vue {
    @Prop() public args: StocksPageArgs | undefined;
    @Prop(String) public tableKey!: string;
    @Prop(String) public exportFileName!: string;
    public stocksResult: any;
    public sites: { [key: number]: Site } = [];

    public dataSource = new DataSource({
        key: 'id' as keyof Stock,
        load: this.load,
        update: () => Promise.resolve(),
    });

    private actions: StocksActions = {
        accept: items => this.storeAgreements(items),
        refuse: (items, defComment) =>
            this.storeZeroAgreements(items, defComment),
    };

    @Inject(CONTRACTS.FetchService)
    private _fetchService?: FetchServiceContract;
    @Inject(CONTRACTS.WindowsService)
    private _windowsService?: WindowsServiceContract;
    @Inject(CONTRACTS.EventsService)
    private _eventsService?: EventsServiceContract;

    private indicatorsForUpdate: string[] = [
        'freeStockProposalsSm',
        'freeStockProposalsSmValue',
        'freeStockCandidates',
        'freeStockAgreements',
    ];

    public get currentProduct() {
        return ProductsState.current;
    }

    public get defaultTableConfig(): Partial<TableConfig> {
        return defaultTableConfig(this);
    }

    public get columnTemplates() {
        return columnTemplates;
    }

    public get filteredData(): Stock[] {
        const filterExpr = this.getDataGridInstance().getCombinedFilter();
        const items = this.stocksResult?.items as Stock[];
        return filterExpr ? Query(items).filter(filterExpr).toArray() : items;
    }

    public get freeStockProposalComments() {
        const comments = localize('FreeStockProposalComments');
        return Array.isArray(comments) ? comments : [];
    }

    public async mounted() {
        this._eventsService!.on(
            Events.ProcessingFinished,
            this.onProcessingFinished
        );
    }

    public async beforeDestroy() {
        this._eventsService!.off(
            Events.ProcessingFinished,
            this.onProcessingFinished
        );
    }

    public getDataGridInstance(): Table {
        const element = this.$refs['data-grid'] as unknown;
        return (element as DxDataGrid).instance!;
    }

    public onRowUpdated(args: { key: any }) {
        this.getDataGridInstance().selectRows([args.key], true);
    }

    public accept(): Promise<any> {
        return this.process(StocksAction.ACCEPT);
    }

    public refuse(defaultRefuseComment: string): Promise<any> {
        return this.process(StocksAction.REFUSE, defaultRefuseComment);
    }

    public openStockConsumptions() {
        this._windowsService!.showDialog('Not implemented!');
    }

    public openStockRoutes() {
        this._windowsService!.showDialog('Not implemented!');
    }

    private onProcessingFinished = ({ contextId }: any) => {
        if (Number(ContextsState.currentContextId) === Number(contextId)) {
            this.getDataGridInstance().refresh();
        }
    };

    private load() {
        return this.makeQuery().then((data: StructuredResult<Stock>) => {
            if (!data) {
                return {
                    data: [],
                    totalCount: 0,
                };
            }
            this.transform(data);
            this.stocksResult = data;
            return {
                data: this.stocksResult.items,
                totalCount: this.stocksResult.items.length,
            };
        });
    }

    private makeQuery(): Promise<StructuredResult<Stock>> {
        const contextId =
            this.args!.contextId || ContextsState.currentContextId;
        let url = `/contexts/${contextId}`;
        if (this.args!.packId) {
            url += `/consumptions/available-stocks?packId=${this.args!.packId}`;
        } else if (this.args!.freeStockProposalsSm) {
            url += `/freeStockProposalsSm?${encodeParams(this.makeFilter())}`;
        } else if (this.args!.freeStockCandidates) {
            url += `/stocks-for-integration?${encodeParams(this.makeFilter())}`;
        } else {
            url += `/stocks?${encodeParams(this.makeFilter())}`;
        }

        return this._fetchService!.get<StructuredResult<Stock>>(url);
    }

    private makeFilter() {
        const filter: any = {};

        if (this.currentProduct) {
            filter.productAlias = this.currentProduct.alias;
        }

        if (this.args!.unused) {
            filter.unused = this.args!.unused;
        }

        if (this.args!.hasProject) {
            filter.hasProject = this.args!.hasProject;
        }

        if (this.args!.minUsage) {
            filter.minUsage = this.args!.minUsage;
        }

        if (this.args!.siteAlias) {
            filter.siteAlias = this.args!.siteAlias;
        }

        return filter;
    }

    private transform(data: StructuredResult<Stock>) {
        if (!(data && data.items)) {
            return;
        }

        const unusedReasons: { [key: string]: string } = {
            wrongDate: localize('UnusedReasonWrongDate'),
            nonActive: localize('UnusedReasonNonActive'),
            default: localize('UnusedReasonDefault'),
        };

        const freeStockReasons: { [key: string]: string } = {
            freeStockCandidate: localize('FreeStockReasonFreeStockCandidate'),
            workClosed: localize('FreeStockReasonWorkClosed'),
        };

        const objectValueStatuses: { [key: string]: string } = {
            emptyPlanValue: localize('ObjectValueStatusEmptyPlanValue'),
            stockIsLess: localize('ObjectValueStatusStockIsLess'),
            stockIsMore: localize('ObjectValueStatusStockIsMore'),
        };

        if (data.products) {
            for (const key of Object.keys(data.products)) {
                const product = data.products[+key];
                if (product.minUsage != null) {
                    product.minUsage =
                        product.minUsage / product.displayDenominator;
                }
            }
        }

        if (data.sites) {
            this.sites = data.sites;
        }

        for (let index = 0; index < data.items.length; index++) {
            const item = data.items[index];
            item.id = index;

            lookupField(item, 'product', data.products);
            lookupField(item, 'site', this.sites);

            item.agreementStatusName = '-';

            if (item.unusedReason) {
                item.unusedReason = unusedReasons[item.unusedReason];
            }

            if (item.freeStockReason) {
                item.freeStockReason = freeStockReasons[item.freeStockReason];
            }

            if (item.source) {
                item.source.site = data.sites![item.source.siteId];
            }

            if (item.destination) {
                item.destination.site = data.sites![item.destination.siteId];
            }

            if (!item.description) {
                item.description = '';
            }

            if (!item.objectPlanValue) {
                const key = 'emptyPlanValue';
                item.objectValueStatus = objectValueStatuses[key];
            } else {
                if (
                    !item.objectStockValue ||
                    item.objectPlanValue > item.objectStockValue
                ) {
                    const key = 'stockIsLess';
                    item.objectValueStatus = objectValueStatuses[key];
                }
                if (
                    item.objectStockValue &&
                    item.objectPlanValue < item.objectStockValue
                ) {
                    const key = 'stockIsMore';
                    item.objectValueStatus = objectValueStatuses[key];
                }
            }

            if (item.sourceTypeId) {
                item.sourceType = data.resourceTypes[item.sourceTypeId];
            }

            if (item.consignmentId) {
                item.consignment = data.strings[item.consignmentId];
            }

            // Calculate agreementStatusName
            if (item.reviewDeadline) {
                item.agreementStatusName = localize(
                    'TitleButtonAgreementDelayed'
                );
            }
        }
    }

    private process(
        action: StocksAction,
        defaultComment?: string
    ): Promise<any> {
        const items = getModifiedItems<Stock>(this.getDataGridInstance());
        if (items.length > 0) {
            const operation = this.actions[action];
            if (operation) {
                return operation(items, defaultComment);
            }
        }
        return Promise.resolve().then(() =>
            IndicatorsState.fetchIndicators(this.indicatorsForUpdate)
        );
    }

    private createAgreements(
        items: Stock[],
        zero: boolean = false
    ): FreeStockAgreement[] {
        return items.map(
            item =>
                ({
                    quantity: zero ? 0 : item.originQuantity,
                    siteId: item.site.alias,
                    productId: item.product.alias,
                    packAlias: item.packAlias,
                    owner: item.source!.owner,
                    project: item.source!.project,
                    object: item.source!.object,
                    description: item.description,
                    reason: item.freeStockReason,
                    price: item.salesPrice,
                    type: item.sourceType ? item.sourceType.alias : null,
                    externalData: item.externalData,
                } as FreeStockAgreement)
        );
    }

    private storeAgreements(
        items: Stock[],
        zero: boolean = false
    ): Promise<any> {
        const data = this.createAgreements(items, zero);
        return this._fetchService!.post(
            `/contexts/${ContextsState.currentContextId}/free-stock-agreements`,
            data
        ).then(() => {
            this.getDataGridInstance().clearSelection();
            removeItemsFromView(this.getDataGridInstance(), items);
        });
    }

    private storeZeroAgreements(
        items: Stock[],
        defaultComment?: string
    ): Promise<any> {
        if (checkField<Stock>(items, 'description', defaultComment)) {
            return this.storeAgreements(items, true);
        }
        return this._windowsService!.showDialog(
            localize('RefuseAgreementsFail')
        );
    }

    private showRoutes(data: any) {
        this._windowsService!.update('microeconomics-details-page', {
            packAlias: data.data.packAlias,
            direction: 'In',
            routeIndex: 0,
        } as MicroeconomicsPageArgs);
    }
}
